import * as React from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  TextField,
  CircularProgress,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  PaperProps
} from "@mui/material";
import { useState } from "react";
import { useDataProvider, useRefresh } from "react-admin";
import Transformer from "src/common/Transformer";
import { ShippingMethodProps } from "src/resources/sales/interface";
import { ShippingMethodEnum, OwnAccountEnum, CourierEnum, OwnAccountShippingMethodServiceEnum, ShippingMethodServiceEnum } from "src/common/enum";
import { useForm } from "react-hook-form";
import { SelectField } from "src/components/input";
import Draggable from 'react-draggable';

interface ShippingMethodFormModalProps {
  method: ShippingMethodProps;
  open: boolean;
  setOpen: any;
  title: string;
  path: string;
}

const PaperComponent = (props: PaperProps) => {
    return (
        <Draggable
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} />
        </Draggable>
    );
}

export const ShippingMethodFormModal = (props: ShippingMethodFormModalProps) => {
    const { method, open, setOpen, title, path } = props;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { register, control, handleSubmit, getValues, setValue, formState: { errors }} = useForm<ShippingMethodProps>({
        defaultValues: method
    });
    const dataProvider = useDataProvider();
    const refresh = useRefresh();

    const transform = (data) => {
        Transformer(data, {
        remove: ["createdAt", "id", "updatedAt", "description", "name"],
        update: {},
        });
        return data;
    };

    const update = async (data: ShippingMethodProps) => {
        try {
            let formData = {...data};
        
            if (formData.type !== "own_account") {
                delete formData["additional"]; 
            } 

            setLoading(true);
            setError(null);
            dataProvider
            .update(path, {
                id: "",
                data: transform({...formData}),
                previousData: method,
            })
            .then((res) => {
                setLoading(false);
                refresh();
                setOpen(false);
            })
            .catch((error) => {
                setError(error.message);
                setLoading(false);
            });
        } catch (e) {
            console.log(e);
        }
    }

    return (
        <Dialog
            onClose={() => setOpen(false)}
            open={open}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
            fullWidth={true}
            maxWidth="sm"
        >
            <form onSubmit={handleSubmit(update)}>
            <DialogTitle sx={{padding: "18px"}}>
                <Typography variant="body1" sx={{fontSize: "20px", fontWeight: "500"}}>{title}</Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => setOpen(false)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                    >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{padding: "18px !important"}}>
                <Grid container item sm={12} rowSpacing={1} columnSpacing={2}>
                    <Grid item sm={12} md={12} sx={{marginBottom: "18px"}}>
                        <SelectField
                            label="Type"
                            size="small"
                            value={getValues("type") || ""}
                            error={!!errors.type}
                            helperText={(errors.type as any)?.message}
                            required
                            choices={ShippingMethodEnum}
                            {...register("type", {
                                required: "Field is required"
                            })}
                            onChange={(e) => {
                               setValue('type', e.target.value, { shouldValidate: true });
                            }}
                        />
                    </Grid>  

                    {getValues("type") == "own_account" &&
                    <>
                    <Grid item sm={12} md={4} sx={{marginBottom: "18px"}}>
                        <SelectField
                            label="Account"
                            size="small"
                            value={getValues("additional.account") || ""}
                            error={!!errors?.additional?.account}
                            helperText={errors?.additional?.account?.message}
                            required={getValues("type") === "own_account" && true}
                            choices={OwnAccountEnum}
                            {...register("additional.account", {
                                required: getValues("type") === "own_account" ? "Field is required" : false
                            })}
                            onChange={(e) => {
                                setValue('additional.account', e.target.value, { shouldValidate: true });
                            }}
                        />
                    </Grid>
                    <Grid item sm={12} md={4} sx={{marginBottom: "18px"}}>
                        <SelectField
                            label="Courier"
                            size="small"
                            value={getValues("additional.courier") || ""}
                            error={!!errors?.additional?.courier}
                            helperText={errors?.additional?.courier?.message}
                            required={getValues("type") === "own_account" && true}
                            choices={CourierEnum}
                            {...register("additional.courier", {
                                required: getValues("type") === "own_account" ? "Field is required" : false
                            })}
                            onChange={(e) => {
                                setValue('additional.courier', e.target.value, { shouldValidate: true });
                            }}
                        />
                    </Grid>
                    <Grid item sm={12} md={4} sx={{marginBottom: "18px"}}>
                        <SelectField
                            label="Service"
                            size="small"
                            value={getValues("additional.service") || ""}
                            error={!!errors?.additional?.service}
                            helperText={errors?.additional?.service?.message}
                            required={getValues("type") === "own_account" && true}
                            choices={OwnAccountShippingMethodServiceEnum}
                            {...register("additional.service", {
                                required: getValues("type") === "own_account" ? "Field is required" : false
                            })}
                            onChange={(e) => {
                                setValue('additional.service', e.target.value, { shouldValidate: true });
                            }}
                        />
                    </Grid>
                    <Grid item sm={12} md={4} sx={{marginBottom: "18px"}}>
                        <TextField
                            label="Account Number"
                            variant="outlined"
                            size="small"
                            error={!!errors?.additional?.account_number}
                            helperText={errors?.additional?.account_number?.message}
                            required={getValues("type") === "own_account" && true}
                            {...register("additional.account_number", {
                                required: getValues("type") === "own_account" ? "Field is required" : false
                            })}
                        />
                    </Grid>
                    </>
                    }

                    { error &&
                    <Grid item sm={12} md={12} sx={{display: "flex", marginTop: "18px"}}>
                        <Typography variant="subtitle2" color="error">{error}</Typography>
                    </Grid>                     
                    } 
                </Grid>
            </DialogContent>
            <DialogActions sx={{padding: "18px"}}>
                <Button
                    variant="outlined"
                    onClick={() => {
                    setOpen(false);
                    }}
                >
                    Cancel
                </Button>
                <Button variant="contained" type="submit" disabled={loading}>
                    {loading && <CircularProgress size={18} thickness={2} sx={{ fontSize: "18px", color: "#ffffff", marginRight: "8px"}}/>}
                    Update
                </Button>
            </DialogActions>
            </form>
        </Dialog>
    );
};
